import {
  Boxes,
  CircleDot,
  CircleStop,
  ChevronUp,
  Hand,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  MoreHorizontal,
  Settings,
  Shield,
  SmilePlus,
  Users,
  Video,
  VideoOff,
  Volume2,
  PencilLine,
  LayoutGrid,
  Maximize,
  UserPlus,
  PictureInPicture2,
  Coins,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { REACTIONS } from '../../shared/protocol';
import { MenuItem, Popover } from '../components/ui';
import { playTestSound } from '../lib/audio';
import { deviceLabel, supports, useDevices } from '../lib/media';
import { updateSettings, useSettings } from '../lib/settings';
import { useStore } from '../lib/store';
import { toast } from '../lib/toast';
import type { MeetingSession } from '../meeting/MeetingSession';
import type { SettingsTab } from '../settings/SettingsDialog';
import { floatingSupported, openFloating } from './floating';

interface Props {
  session: MeetingSession;
  participantsOpen: boolean;
  chatOpen: boolean;
  breakoutOpen: boolean;
  onToggleBreakout(): void;
  redisOpen: boolean;
  onToggleRedis(): void;
  boardShown: boolean;
  onToggleBoard(): void;
  onToggleParticipants(): void;
  onToggleChat(): void;
  onOpenSettings(tab: SettingsTab): void;
  onLeave(): void;
  onRename(): void;
  onInvite(): void;
  onOpenShare(): void;
  onToggleView(): void;
  onToggleFullscreen(): void;
}

function ToolButton({
  icon,
  label,
  onClick,
  active,
  off,
  className = '',
  count,
  alert,
  shortcut,
  btnRef,
  expanded,
}: {
  icon: ReactNode;
  label: string;
  onClick(): void;
  active?: boolean;
  off?: boolean;
  className?: string;
  count?: number;
  alert?: boolean;
  shortcut?: string;
  btnRef?: React.Ref<HTMLButtonElement>;
  expanded?: boolean;
}) {
  return (
    <button
      ref={btnRef}
      type="button"
      className={`tb-btn ${active ? 'active' : ''} ${off ? 'off' : ''} ${className}`}
      onClick={onClick}
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-label={label}
      aria-pressed={active}
      aria-expanded={expanded}
    >
      <span className="tb-icon">{icon}</span>
      <span className="tb-label">{label}</span>
      {count !== undefined && count > 0 && <span className={`tb-count ${alert ? 'alert' : ''}`}>{count > 99 ? '99+' : count}</span>}
    </button>
  );
}

type Menu = 'audio' | 'video' | 'reactions' | 'security' | 'more' | null;

export function Toolbar(p: Props) {
  const { session } = p;
  const s = useStore(session.store);
  const settings = useSettings();
  const devices = useDevices();
  const [menu, setMenu] = useState<Menu>(null);
  const audioCaret = useRef<HTMLButtonElement>(null);
  const videoCaret = useRef<HTMLButtonElement>(null);
  const reactRef = useRef<HTMLButtonElement>(null);
  const securityRef = useRef<HTMLButtonElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const self = s.participants[s.selfId!];
  const amHost = self?.role === 'host';
  const count = Object.keys(s.participants).length;
  const close = () => setMenu(null);
  const toggle = (m: Menu) => setMenu((cur) => (cur === m ? null : m));
  const iAmSharing = !!s.screenStream;
  const pick = (patch: Parameters<typeof updateSettings>[0]) => {
    updateSettings(patch);
    close();
  };
  const roomPatch = (patch: Parameters<MeetingSession['host']>[0]) => session.host(patch);

  return (
    <footer className="toolbar" aria-label="Controles de la reunión">
      <div className="toolbar-group">
        <div className="tb-split">
          <ToolButton
            icon={s.audioMuted ? <MicOff size={22} /> : <Mic size={22} />}
            label={s.audioMuted ? 'Activar audio' : 'Silenciar'}
            off={s.audioMuted}
            onClick={() => void session.toggleAudio()}
            shortcut="Alt+A"
          />
          <button ref={audioCaret} type="button" className="tb-caret" aria-label="Opciones de audio" aria-haspopup="menu" aria-expanded={menu === 'audio'} onClick={() => toggle('audio')}>
            <ChevronUp size={14} />
          </button>
        </div>
        <div className="tb-split">
          <ToolButton
            icon={s.videoOn ? <Video size={22} /> : <VideoOff size={22} />}
            label={s.videoOn ? 'Detener vídeo' : 'Iniciar vídeo'}
            off={!s.videoOn}
            onClick={() => void session.toggleVideo()}
            shortcut="Alt+V"
          />
          <button ref={videoCaret} type="button" className="tb-caret" aria-label="Opciones de vídeo" aria-haspopup="menu" aria-expanded={menu === 'video'} onClick={() => toggle('video')}>
            <ChevronUp size={14} />
          </button>
        </div>
      </div>

      <div className="toolbar-group toolbar-center">
        <ToolButton
          icon={<Users size={22} />}
          label="Participantes"
          active={p.participantsOpen}
          onClick={p.onToggleParticipants}
          count={amHost && s.waiting.length ? s.waiting.length : count}
          alert={amHost && s.waiting.length > 0}
          shortcut="Alt+U"
        />
        <ToolButton icon={<MessageSquare size={22} />} label="Chat" active={p.chatOpen} onClick={p.onToggleChat} count={s.unread} alert shortcut="Alt+H" />
        <ToolButton btnRef={reactRef} className="hide-narrow" icon={<SmilePlus size={22} />} label="Reacciones" onClick={() => toggle('reactions')} active={self?.handRaised} expanded={menu === 'reactions'} />
        <ToolButton
          className={`share ${iAmSharing ? 'active' : ''}`}
          icon={<MonitorUp size={18} />}
          label={iAmSharing ? 'Dejar de compartir' : 'Compartir'}
          onClick={() => (iAmSharing ? session.stopShare() : p.onOpenShare())}
          shortcut="Alt+S"
        />
        {session.canRecord() && (
          <ToolButton
            className={`hide-narrow ${s.recording ? 'recording' : ''}`}
            icon={s.recording ? <CircleStop size={22} /> : <CircleDot size={22} />}
            label={s.recording ? 'Detener grabación' : 'Grabar'}
            active={!!s.recording}
            onClick={() => (s.recording ? void session.stopRecording() : session.startRecording())}
          />
        )}
        {amHost && (
          <ToolButton
            className="hide-narrow"
            icon={<Boxes size={22} />}
            label="Salas"
            active={p.breakoutOpen}
            onClick={p.onToggleBreakout}
            count={s.breakout.status !== 'idle' ? s.breakout.rooms.length : 0}
          />
        )}
        {amHost && <ToolButton btnRef={securityRef} className="hide-narrow" icon={<Shield size={22} />} label="Seguridad" onClick={() => toggle('security')} active={s.locked} expanded={menu === 'security'} />}
        <ToolButton btnRef={moreRef} icon={<MoreHorizontal size={22} />} label="Más" onClick={() => toggle('more')} expanded={menu === 'more'} />
      </div>

      <div className="toolbar-group">
        <button type="button" className="btn btn-danger tb-end" onClick={p.onLeave}>
          {amHost ? 'Finalizar' : 'Salir'}
        </button>
      </div>

      {menu === 'audio' && (
        <Popover anchor={audioCaret} onClose={close} label="Opciones de audio">
          <div className="menu-label">Seleccionar un micrófono</div>
          {devices.audioInputs.length === 0 && <div className="menu-item muted">No hay micrófonos disponibles</div>}
          {devices.audioInputs.map((d, i) => (
            <MenuItem key={d.deviceId || i} checked={(settings.audioInputId || 'default') === d.deviceId} onSelect={() => pick({ audioInputId: d.deviceId })}>
              {deviceLabel(d, i, 'Micrófono')}
            </MenuItem>
          ))}
          {supports.outputSelection && (
            <>
              <div className="menu-sep" />
              <div className="menu-label">Seleccionar un altavoz</div>
              {devices.audioOutputs.map((d, i) => (
                <MenuItem key={d.deviceId || i} checked={(settings.audioOutputId || 'default') === d.deviceId} onSelect={() => pick({ audioOutputId: d.deviceId })}>
                  {deviceLabel(d, i, 'Altavoz')}
                </MenuItem>
              ))}
            </>
          )}
          <div className="menu-sep" />
          <MenuItem
            icon={<Volume2 size={16} />}
            onSelect={() => {
              close();
              playTestSound(settings.audioOutputId).catch(() => toast('No se pudo reproducir el sonido de prueba', 'error'));
            }}
          >
            Probar altavoz
          </MenuItem>
          <MenuItem
            icon={<Settings size={16} />}
            onSelect={() => {
              close();
              p.onOpenSettings('audio');
            }}
          >
            Configuración de audio…
          </MenuItem>
        </Popover>
      )}

      {menu === 'video' && (
        <Popover anchor={videoCaret} onClose={close} label="Opciones de vídeo">
          <div className="menu-label">Seleccionar una cámara</div>
          {devices.videoInputs.length === 0 && <div className="menu-item muted">No hay cámaras disponibles</div>}
          {devices.videoInputs.map((d, i) => {
            const current = settings.videoInputId || s.cameraTrack?.getSettings().deviceId || devices.videoInputs[0]?.deviceId;
            return (
              <MenuItem key={d.deviceId || i} checked={current === d.deviceId} onSelect={() => pick({ videoInputId: d.deviceId })}>
                {deviceLabel(d, i, 'Cámara')}
              </MenuItem>
            );
          })}
          <div className="menu-sep" />
          <MenuItem checked={settings.mirrorVideo} onSelect={() => pick({ mirrorVideo: !settings.mirrorVideo })}>
            Reflejar mi vídeo
          </MenuItem>
          <MenuItem checked={settings.hdVideo} onSelect={() => pick({ hdVideo: !settings.hdVideo })}>
            Vídeo HD
          </MenuItem>
          <MenuItem checked={settings.hideSelfView} onSelect={() => pick({ hideSelfView: !settings.hideSelfView })}>
            Ocultar mi propia vista
          </MenuItem>
          <div className="menu-sep" />
          <MenuItem
            icon={<Settings size={16} />}
            onSelect={() => {
              close();
              p.onOpenSettings('video');
            }}
          >
            Configuración de vídeo…
          </MenuItem>
        </Popover>
      )}

      {menu === 'reactions' && (
        <Popover anchor={reactRef} onClose={close} label="Reacciones">
          <div className="reaction-row">
            {REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                role="menuitem"
                aria-label={`Reaccionar con ${emoji}`}
                onClick={() => {
                  session.react(emoji);
                  close();
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
          <div className="menu-sep" />
          <MenuItem
            icon={<Hand size={16} />}
            onSelect={() => {
              session.setHandRaised(!self?.handRaised);
              close();
            }}
          >
            {self?.handRaised ? 'Bajar la mano' : 'Levantar la mano'} <span className="kbd">Alt+Y</span>
          </MenuItem>
        </Popover>
      )}

      {menu === 'security' && amHost && (
        // On phones the Seguridad button is hidden: the menu then opens from «Más».
        <Popover anchor={securityRef.current?.offsetParent ? securityRef : moreRef} onClose={close} label="Seguridad">
          <MenuItem checked={s.locked} onSelect={() => roomPatch({ action: 'lock', locked: !s.locked })}>
            Bloquear reunión
          </MenuItem>
          <MenuItem checked={s.roomSettings.waitingRoom} onSelect={() => roomPatch({ action: 'settings', settings: { waitingRoom: !s.roomSettings.waitingRoom } })}>
            Habilitar sala de espera
          </MenuItem>
          <div className="menu-sep" />
          <div className="menu-label">Permitir a los participantes</div>
          <MenuItem checked={s.roomSettings.allowShare === 'all'} onSelect={() => roomPatch({ action: 'settings', settings: { allowShare: s.roomSettings.allowShare === 'all' ? 'host' : 'all' } })}>
            Compartir pantalla
          </MenuItem>
          <MenuItem checked={s.roomSettings.allowChat} onSelect={() => roomPatch({ action: 'settings', settings: { allowChat: !s.roomSettings.allowChat } })}>
            Chatear
          </MenuItem>
          <MenuItem checked={s.roomSettings.allowRename} onSelect={() => roomPatch({ action: 'settings', settings: { allowRename: !s.roomSettings.allowRename } })}>
            Cambiar su nombre
          </MenuItem>
          <MenuItem checked={s.roomSettings.allowUnmute} onSelect={() => roomPatch({ action: 'settings', settings: { allowUnmute: !s.roomSettings.allowUnmute } })}>
            Reactivar su audio
          </MenuItem>
          <MenuItem checked={s.roomSettings.allowRecording} onSelect={() => roomPatch({ action: 'settings', settings: { allowRecording: !s.roomSettings.allowRecording } })}>
            Grabar la reunión
          </MenuItem>
          <div className="menu-sep" />
          <MenuItem checked={s.roomSettings.muteOnEntry} onSelect={() => roomPatch({ action: 'settings', settings: { muteOnEntry: !s.roomSettings.muteOnEntry } })}>
            Silenciar a los participantes al entrar
          </MenuItem>
        </Popover>
      )}

      {menu === 'more' && (
        <Popover anchor={moreRef} onClose={close} label="Más opciones">
          <div className="only-narrow">
            <MenuItem
              icon={<Hand size={16} />}
              onSelect={() => {
                session.setHandRaised(!self?.handRaised);
                close();
              }}
            >
              {self?.handRaised ? 'Bajar la mano' : 'Levantar la mano'}
            </MenuItem>
            <div className="reaction-row">
              {REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  role="menuitem"
                  aria-label={`Reaccionar con ${emoji}`}
                  onClick={() => {
                    session.react(emoji);
                    close();
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {amHost && (
              <>
                <MenuItem checked={s.locked} onSelect={() => roomPatch({ action: 'lock', locked: !s.locked })}>
                  Bloquear reunión
                </MenuItem>
                <MenuItem checked={s.roomSettings.waitingRoom} onSelect={() => roomPatch({ action: 'settings', settings: { waitingRoom: !s.roomSettings.waitingRoom } })}>
                  Sala de espera
                </MenuItem>
                <MenuItem icon={<Shield size={16} />} onSelect={() => setMenu('security')}>
                  Seguridad y permisos…
                </MenuItem>
                <MenuItem
                  icon={<Boxes size={16} />}
                  onSelect={() => {
                    close();
                    p.onToggleBreakout();
                  }}
                >
                  Salas para grupos pequeños
                </MenuItem>
              </>
            )}
            <div className="menu-sep" />
          </div>
          <MenuItem
            icon={<UserPlus size={16} />}
            onSelect={() => {
              close();
              p.onInvite();
            }}
          >
            Invitar
          </MenuItem>
          {amHost && (
            <MenuItem
              icon={<Coins size={16} />}
              onSelect={() => {
                close();
                p.onToggleRedis();
              }}
            >
              {s.redistribution.status === 'off' ? 'Redistribución…' : 'Configurar la redistribución…'}
            </MenuItem>
          )}
          {s.redistribution.status !== 'off' && (
            <MenuItem
              icon={<Coins size={16} />}
              onSelect={() => {
                close();
                p.onToggleBoard();
              }}
            >
              {p.boardShown ? 'Ver los vídeos' : 'Ver el tablero de redistribución'}
            </MenuItem>
          )}
          {session.canRecord() && (
            <MenuItem
              icon={s.recording ? <CircleStop size={16} /> : <CircleDot size={16} />}
              onSelect={() => {
                close();
                if (s.recording) void session.stopRecording();
                else session.startRecording();
              }}
            >
              {s.recording ? 'Detener grabación' : 'Grabar'}
            </MenuItem>
          )}
          {floatingSupported() && (
            <MenuItem
              icon={<PictureInPicture2 size={16} />}
              onSelect={() => {
                close();
                void openFloating(session).then((ok) => !ok && toast('No se pudo abrir la ventana flotante', 'warning'));
              }}
            >
              Ventana flotante
            </MenuItem>
          )}
          <MenuItem
            icon={<LayoutGrid size={16} />}
            onSelect={() => {
              p.onToggleView();
              close();
            }}
          >
            Cambiar vista (galería / orador)
          </MenuItem>
          <MenuItem
            icon={<Maximize size={16} />}
            onSelect={() => {
              p.onToggleFullscreen();
              close();
            }}
          >
            Pantalla completa <span className="kbd">Alt+F</span>
          </MenuItem>
          <MenuItem
            icon={<PencilLine size={16} />}
            disabled={!amHost && !s.roomSettings.allowRename}
            onSelect={() => {
              close();
              p.onRename();
            }}
          >
            Cambiar mi nombre
          </MenuItem>
          <MenuItem
            icon={<Settings size={16} />}
            onSelect={() => {
              close();
              p.onOpenSettings('general');
            }}
          >
            Configuración
          </MenuItem>
        </Popover>
      )}
    </footer>
  );
}
